<template>
  <v-dialog
    max-width="600"
    class="d-block"
    :value="show"
    @click:outside="close"
    @keydown.esc="close"
  >
    <v-card class="px-6 py-4">
      <v-card-title class="ma-0 pa-0 mb-6">{{
        $t('people.edit_account')
      }}</v-card-title>
      <v-card-text class="ma-0 pa-0">
        <v-form
          v-model="isFormValid"
          ref="account"
          data-testid="editAccount-form"
        >
          <div class="d-flex mb-2">
            <v-text-field
              outlined
              dense
              required
              data-private
              v-model="form.displayname"
              class="me-2 "
              :rules="requiredFieldRules"
              :label="`${$t('common.users.first_name')}*`"
            />
            <v-text-field
              outlined
              dense
              v-model="form.familyname"
              data-private
              :label="$t('common.users.last_name')"
            />
          </div>
          <v-text-field
            outlined
            dense
            v-model="form.email"
            required
            class="mb-2"
            data-testid="editAccount-textField-email"
            :hint="emailHint"
            :label="`${$t('common.email_one')}*`"
            :persistentHint="isEmailReadonly"
            :readonly="isEmailReadonly"
            :rules="emailRules"
          />
          <v-text-field
            outlined
            dense
            v-model="form.position"
            :label="$t('profile_settings.job_position')"
          />
          <v-text-field
            outlined
            dense
            v-model="form.employeeId"
            :label="$t('profile_settings.employee_id')"
            placeholder="153682"
          />
          <v-divider class="mt-1 mb-4" />

          <LocationSelector
            data-testid="editAccountDialog-selector-site"
            data-tourid="edit-user-location-selector"
            key="edit-user-location-selector"
            :availableSiteIds="availableSiteIds"
            :organizationId="organizationId"
            :location="location"
            @change="locationSelectorChange"
          />
        </v-form>
        <v-divider class="mt-1 mb-6" data-testid="editAccount-divider-skills" />

        <SkillsEditor
          data-testid="editAccount-skillsEditor"
          :skills="skills"
          :label="$t('profile_settings.skill_other')"
          :maxSkills="$constants.profile.skillsMaxCount"
          @update="updateSkills"
          @update:hasPendingText="onPendingSkillsChange"
        />
      </v-card-text>

      <v-card-actions class="align-center justify-end ma-0 pa-0 mt-4">
        <Button
          plain
          color="grey"
          :text="$t('common.button.cancel')"
          @click="close"
        />
        <Button
          color="primary"
          data-testid="editAccount-button-save"
          :disabled="!isFormValid || !location.site || hasPendingSkills"
          :text="$t('common.button.save')"
          @click="onSubmit"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'

import LocationSelector from '@/components/LocationSelector/LocationSelector.vue'
import SkillsEditor from '@/components/Skills/SkillsEditor.vue'
import Button from '@/components/UI/Button'

import mixin from '@/mixins/mixin.vue'
import validationHelper from '@/lib/validationHelper.js'

import _ from 'lodash'
import { utils } from 'frontend-js-shared'

export default {
  name: 'EditAccountDialog',

  components: {
    LocationSelector,
    SkillsEditor,
    Button,
  },

  mixins: [mixin],

  props: {
    show: {
      type: Boolean,
      required: true,
      default: false,
    },
    account: {
      type: Object,
      required: true,
    },
    organizationId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      form: {},
      location: {
        organization: '',
        site: '',
        tags: [],
      },
      isFormValid: null,
      hasPendingSkills: false,
      emailRules: [
        email =>
          !this.$constants.organization.domainsBlacklist.includes(
            this.getDomain(email)
          ) || this.$t('organization.domain_is_insecure_to_add'),

        email =>
          validationHelper.isValidEmailAddress(email) ||
          this.$t('validation.user.please_enter_a_valid_email_address'),
      ],
      requiredFieldRules: [
        value =>
          !!value?.trim?.() ||
          this.$t('validation.form.this_field_is_required'),
      ],
    }
  },

  computed: {
    ...mapGetters({
      authorizedSites: 'authorizedSiteDetails',
      currentUser: 'account',
    }),

    availableSiteIds() {
      if (!this.currentUser?.isSiteAdmin) {
        return []
      }

      return (this.authorizedSites ?? []).map(site => utils.getEntityId(site))
    },

    skills() {
      return this.form.skills ?? []
    },

    isEmailReadonly() {
      return this.account?.isPrimary === false
    },

    emailHint() {
      return this.isEmailReadonly
        ? this.$t('people.secondary_account_email_readonly_hint')
        : ''
    },
  },

  mounted() {
    if (!_.isEmpty(this.account)) {
      this.form = _.cloneDeep(this.account)
      this.location = {
        organization: this.account?.organization?._id,
        site: this.account?.site?._id,
        tags: this.account?.tags,
      }
    }
  },

  methods: {
    getDomain(email) {
      return email?.replace?.(/.*@/, '') ?? ''
    },

    close() {
      this.$emit('cancel')
      this.form = {}
    },

    updateSkills({ skills }) {
      this.form = { ...this.form, skills }
    },

    onPendingSkillsChange(hasPendingSkills) {
      this.hasPendingSkills = hasPendingSkills
    },

    locationSelectorChange(newLocation) {
      this.location = {
        ...newLocation,
        organization: this.account?.organization?._id,
      }
    },

    onSubmit() {
      const formData = this.isEmailReadonly
        ? _.omit(this.form, 'email')
        : this.form

      this.$emit('update', { ...formData, location: this.location })
    },
  },
}
</script>
